import { useEffect, useRef, useState } from "react";
import type { CSSProperties, ReactNode, RefObject } from "react";
import {
  AlertTriangle,
  Copy,
  Check,
  Download,
  GitBranch,
  ImagePlus,
  Info,
  MoreHorizontal,
  Recycle,
  Trash2,
} from "lucide-react";
import {
  api,
  formatDuration,
  formatElapsed,
  formatFrame,
  formatMicroUsd,
  isInFlight,
  type Generation,
  type ImageAsset,
} from "../api";
import { AsyncButton, ErrorNote, IconButton, Modal, describeError } from "./ui";

/**
 * A print comes up once.
 *
 * The develop transition is the one theatrical thing in this interface (§21), and the fastest way
 * to make it tiresome would be to replay it every time a card re-renders or scrolls back into
 * view. This set remembers, for the life of the tab, which images have already come up.
 */
const developedImageIds = new Set<string>();

/** Below this the action row is three targets and a "More" sheet, never seven icons (§21.1). */
const PHONE_QUERY = "(max-width: 760px)";

/** How many actions stay on the card itself when the screen is narrow. */
const COMPACT_ACTION_COUNT = 3;

/** What a tile assumes about a frame that has no image yet and no readable size. */
const DEFAULT_TILE_ASPECT = 1;

interface FrameAction {
  key: string;
  label: string;
  icon: ReactNode;
  onClick: () => void;
  disabled?: boolean;
  disabledReason?: string;
  className?: string;
}

/** Everything a frame can be asked to do, shared by the card and the tile. */
export interface FrameHandlers {
  onSelect: (generation: Generation) => void;
  onZoom: (image: ImageAsset) => void;
  onUseAsInput: (image: ImageAsset) => void;
  onBranch: (generation: Generation) => void;
  /** Put this frame's prompt, model, parameters and references back in the composer. */
  onRecycle: (generation: Generation) => void;
  onDownload: (image: ImageAsset) => void;
  onDelete: (generation: Generation) => void;
  onRetried: () => void;
}

interface FrameCardProps extends FrameHandlers {
  generation: Generation;
  /** Resolved by the caller from the board's generation list; frames are referred to by number. */
  parentSequence?: number;
  selected: boolean;
}

export function FrameCard({ generation, parentSequence, selected, ...handlers }: FrameCardProps) {
  const inFlight = isInFlight(generation.status);
  const elapsed = useElapsed(generation.created_at, inFlight);
  const compact = useCompactActions();
  const [moreOpen, setMoreOpen] = useState(false);
  const freshOutputIds = useFreshOutputs(generation);

  const firstOutput: ImageAsset | undefined = generation.outputs[0];
  const actions = useFrameActions(generation, firstOutput, selected, handlers);

  const shown = compact ? actions.slice(0, COMPACT_ACTION_COUNT) : actions;
  const overflow = compact ? actions.slice(COMPACT_ACTION_COUNT) : [];

  return (
    <article className={generation.status === "failed" ? "frame is-failed" : "frame"}>
      <div className="frame-head">
        <span className="frame-number">{formatFrame(generation.sequence)}</span>
        <span className="chip">{generation.model_display_name}</span>
        <span className="chip">{generation.mode === "edit" ? "Edit" : "Generate"}</span>
        {generation.status === "failed" && <span className="chip warn">Failed</span>}
        {generation.status === "cancelled" && <span className="chip warn">Cancelled</span>}
        {parentSequence !== undefined && (
          <span className="stamp">
            From frame <span className="mono">{formatFrame(parentSequence)}</span>
          </span>
        )}

        <div className="frame-actions">
          <ActionButtons actions={shown} />
          {overflow.length > 0 && (
            <IconButton onClick={() => setMoreOpen(true)} label="More actions">
              <MoreHorizontal size={15} />
            </IconButton>
          )}
        </div>
      </div>

      <p className="frame-prompt">{generation.prompt}</p>

      {inFlight && (
        <div className="frame-body">
          <div className="tray">
            <div className="tray-readout">
              <span className="stamp">Generating</span>
              {/* A real elapsed time. These calls run 10–120 seconds and a spinner says nothing. */}
              <span className="elapsed">{formatElapsed(elapsed)}</span>
              {generation.status === "queued" && <span className="stamp">Waiting for a worker</span>}
            </div>
          </div>
        </div>
      )}

      {generation.status === "failed" && (
        <div className="frame-body">
          <div className="col grow">
            <ErrorNote
              title={generation.error_title ?? "This frame did not finish."}
              detail={generation.error_detail}
            />
            <RetryButton generation={generation} onRetried={handlers.onRetried} />
          </div>
        </div>
      )}

      {generation.status === "cancelled" && (
        <div className="frame-body">
          <p className="notice">This frame was cancelled before it ran.</p>
        </div>
      )}

      {generation.status === "succeeded" && (
        <div className="frame-body">
          <div className="col">
            {generation.outputs.map((image) => (
              <button
                key={image.id}
                type="button"
                className={freshOutputIds.has(image.id) ? "print develop" : "print"}
                onClick={() => handlers.onZoom(image)}
                aria-label={`Enlarge frame ${formatFrame(generation.sequence)}`}
              >
                <img
                  src={image.thumb_url ?? image.content_url}
                  alt={generation.prompt}
                  width={image.width}
                  height={image.height}
                />
              </button>
            ))}
            {generation.response_text && <p className="lede">{generation.response_text}</p>}
          </div>

          <div className="frame-meta">
            {Object.entries(generation.parameters).map(([key, value]) => (
              <MetaRow key={key} label={labelForKey(key)} value={String(value)} reading={isReading(value)} />
            ))}
            {generation.duration_ms !== undefined && (
              <MetaRow label="Took" value={formatDuration(generation.duration_ms)} reading />
            )}
            {/* An estimate, and unpriced models must read as unknown rather than free (§18). */}
            <MetaRow
              label="Est. cost"
              value={formatMicroUsd(generation.cost_micro_usd)}
              title={generation.cost_basis}
              reading
            />
          </div>
        </div>
      )}

      {moreOpen && (
        <MoreSheet generation={generation} actions={overflow} onClose={() => setMoreOpen(false)} />
      )}
    </article>
  );
}

// ── Tiles ──────────────────────────────────────────────────────────────────────────────────

interface FrameTilesProps extends FrameHandlers {
  generation: Generation;
  selected: boolean;
}

/**
 * The same frame as tiles: one per image, nothing but the print and its actions.
 *
 * Each tile carries its aspect ratio as a CSS variable, and the stylesheet turns that into a
 * flex-basis and flex-grow proportional to it. Every tile in a row then gets a width in proportion
 * to its ratio, which is the same as saying every tile in a row gets the same height — a
 * justified layout, in which a landscape and a portrait sit side by side at one row height with
 * neither cropped nor letterboxed.
 */
export function FrameTiles({ generation, selected, ...handlers }: FrameTilesProps) {
  const freshOutputIds = useFreshOutputs(generation);

  if (generation.status !== "succeeded" || generation.outputs.length === 0) {
    return <FrameTile generation={generation} selected={selected} {...handlers} />;
  }

  return (
    <>
      {generation.outputs.map((image) => (
        <FrameTile
          key={image.id}
          generation={generation}
          image={image}
          fresh={freshOutputIds.has(image.id)}
          selected={selected}
          {...handlers}
        />
      ))}
    </>
  );
}

interface FrameTileProps extends FrameHandlers {
  generation: Generation;
  image?: ImageAsset;
  fresh?: boolean;
  selected: boolean;
}

function FrameTile({ generation, image, fresh, selected, ...handlers }: FrameTileProps) {
  const inFlight = isInFlight(generation.status);
  const elapsed = useElapsed(generation.created_at, inFlight);
  const [moreOpen, setMoreOpen] = useState(false);
  const barRef = useRef<HTMLDivElement>(null);
  const actions = useFrameActions(generation, image, selected, handlers);

  // Tiles are as wide as their image is, so a portrait may only have room for two or three
  // targets. The bar measures itself and moves the tail into the sheet, the way a phone does.
  const fit = useFittedActionCount(barRef);
  const allFit = fit === null || fit >= actions.length;
  const shown = allFit ? actions : actions.slice(0, Math.max(0, fit - 1));
  const overflow = allFit ? [] : actions.slice(shown.length);

  const aspect = image ? image.width / image.height : requestedAspect(generation);
  const style = { "--ar": aspect } as CSSProperties;

  const classes = ["tile"];
  if (selected) classes.push("is-selected");
  if (generation.status === "failed") classes.push("is-failed");

  return (
    // The id lets the arrow keys map a tile on screen back to its frame (useTileNavigation).
    <article className={classes.join(" ")} style={style} data-generation-id={generation.id}>
      {image ? (
        <button
          type="button"
          className={fresh ? "tile-print develop" : "tile-print"}
          onClick={() => handlers.onZoom(image)}
          aria-label={`Enlarge frame ${formatFrame(generation.sequence)}`}
        >
          <img
            src={image.thumb_url ?? image.content_url}
            alt={generation.prompt}
            width={image.width}
            height={image.height}
          />
        </button>
      ) : inFlight ? (
        <div className="tile-well tray">
          <div className="tray-readout">
            <span className="elapsed">{formatElapsed(elapsed)}</span>
            <span className="stamp">{generation.status === "queued" ? "Waiting" : "Generating"}</span>
          </div>
        </div>
      ) : generation.status === "failed" ? (
        <div className="tile-well">
          <AlertTriangle size={18} aria-hidden="true" />
          <span className="stamp">Failed</span>
          <RetryButton generation={generation} onRetried={handlers.onRetried} tile />
        </div>
      ) : (
        <div className="tile-well">
          <span className="stamp">
            {generation.status === "cancelled" ? "Cancelled" : "No image"}
          </span>
        </div>
      )}

      <div className="tile-actions" ref={barRef}>
        <ActionButtons actions={shown} />
        {overflow.length > 0 && (
          <IconButton onClick={() => setMoreOpen(true)} label="More actions">
            <MoreHorizontal size={15} />
          </IconButton>
        )}
      </div>

      {moreOpen && (
        <MoreSheet generation={generation} actions={overflow} onClose={() => setMoreOpen(false)} />
      )}
    </article>
  );
}

/**
 * The shape a frame was asked for, so a tile that is still developing takes up the space its
 * print will. Models express this as "1024x1536" or "2:3"; anything else reads as square.
 */
function requestedAspect(generation: Generation): number {
  for (const value of Object.values(generation.parameters)) {
    if (typeof value !== "string") continue;
    const match = /^\s*(\d+)\s*[x:×]\s*(\d+)\s*$/i.exec(value);
    if (!match) continue;
    const width = Number(match[1]);
    const height = Number(match[2]);
    if (width > 0 && height > 0) return width / height;
  }
  return DEFAULT_TILE_ASPECT;
}

// ── Shared pieces ──────────────────────────────────────────────────────────────────────────

function ActionButtons({ actions }: { actions: FrameAction[] }) {
  return (
    <>
      {actions.map((action) => (
        <IconButton
          key={action.key}
          onClick={action.onClick}
          label={action.label}
          className={action.className}
          disabled={action.disabled}
          disabledReason={action.disabledReason}
        >
          {action.icon}
        </IconButton>
      ))}
    </>
  );
}

function MoreSheet({
  generation,
  actions,
  onClose,
}: {
  generation: Generation;
  actions: FrameAction[];
  onClose: () => void;
}) {
  return (
    <Modal title={`Frame ${formatFrame(generation.sequence)}`} onClose={onClose}>
      <div className="list">
        {actions.map((action) => (
          <button
            key={action.key}
            type="button"
            className="list-row"
            disabled={action.disabled}
            title={action.disabled ? action.disabledReason : undefined}
            onClick={() => {
              action.onClick();
              onClose();
            }}
          >
            {action.icon}
            <span className="grow">{action.label}</span>
          </button>
        ))}
      </div>
    </Modal>
  );
}

function RetryButton({
  generation,
  onRetried,
  tile,
}: {
  generation: Generation;
  onRetried: () => void;
  /** In a tile there is no room for the error note; the button itself says the retry failed. */
  tile?: boolean;
}) {
  const [retryError, setRetryError] = useState<{ title: string; detail?: string } | null>(null);

  const retry = async () => {
    setRetryError(null);
    try {
      await api.retryGeneration(generation.id);
      onRetried();
    } catch (error) {
      setRetryError(describeError(error));
    }
  };

  if (tile) {
    return (
      <AsyncButton className="btn ghost" onClick={retry}>
        {retryError ? "Retry failed" : "Retry"}
      </AsyncButton>
    );
  }

  return (
    <>
      {retryError && <ErrorNote title={retryError.title} detail={retryError.detail} />}
      <div className="row">
        <AsyncButton onClick={retry}>Retry</AsyncButton>
      </div>
    </>
  );
}

function MetaRow({
  label,
  value,
  reading,
  title,
}: {
  label: string;
  value: string;
  /** Mono is for readings — a size, a duration, an amount — never for words. */
  reading?: boolean;
  title?: string;
}) {
  return (
    <div className="meta-row">
      <span className="stamp">{label}</span>
      <span className={reading ? "stamp-value" : undefined} title={title}>
        {value}
      </span>
    </div>
  );
}

/** Parameter keys are snake_case on the wire and sentence case on screen. */
function labelForKey(key: string): string {
  const words = key.replace(/_/g, " ");
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** "1536x1024" and 7 are readings; "high" and "auto" are words that should stay in the text face. */
function isReading(value: string | number | boolean): boolean {
  return typeof value === "number" || /^\d/.test(String(value));
}

// ── Hooks ──────────────────────────────────────────────────────────────────────────────────

/**
 * The action list, ordered by how often each is reached for: the first three are what survives
 * on a phone. `image` is the print the per-image actions apply to; the card passes its first
 * output, a tile passes its own.
 */
function useFrameActions(
  generation: Generation,
  image: ImageAsset | undefined,
  selected: boolean,
  handlers: FrameHandlers,
): FrameAction[] {
  const [copied, setCopied] = useState(false);
  const noImage = image === undefined ? "This frame has no image yet." : undefined;

  return [
    {
      key: "inspect",
      label: "Inspect frame",
      icon: <Info size={15} />,
      className: selected ? "btn icon" : "btn ghost icon",
      onClick: () => handlers.onSelect(generation),
    },
    {
      key: "recycle",
      label: "Recycle settings",
      icon: <Recycle size={15} />,
      onClick: () => handlers.onRecycle(generation),
    },
    {
      key: "input",
      label: "Use as input",
      icon: <ImagePlus size={15} />,
      disabled: image === undefined,
      disabledReason: noImage,
      onClick: () => image && handlers.onUseAsInput(image),
    },
    {
      key: "download",
      label: "Download",
      icon: <Download size={15} />,
      disabled: image === undefined,
      disabledReason: noImage,
      onClick: () => image && handlers.onDownload(image),
    },
    {
      key: "branch",
      label: "Branch from here",
      icon: <GitBranch size={15} />,
      disabled: image === undefined,
      disabledReason: noImage,
      onClick: () => handlers.onBranch(generation),
    },
    {
      key: "copy",
      label: copied ? "Prompt copied" : "Copy prompt",
      icon: copied ? <Check size={15} /> : <Copy size={15} />,
      onClick: () => {
        void navigator.clipboard.writeText(generation.prompt).then(() => {
          setCopied(true);
          window.setTimeout(() => setCopied(false), 1500);
        });
      },
    },
    {
      key: "delete",
      label: "Delete frame",
      icon: <Trash2 size={15} />,
      className: "btn ghost icon danger",
      onClick: () => handlers.onDelete(generation),
    },
  ];
}

/**
 * Which of this frame's images have not been shown yet. Decided at render, committed after
 * paint: the class must survive later re-renders unchanged, while the module-level set is only
 * written once the frame has actually been on screen.
 */
function useFreshOutputs(generation: Generation): Set<string> {
  const freshOutputIds = useRef(new Set<string>());
  for (const output of generation.outputs) {
    if (!developedImageIds.has(output.id)) freshOutputIds.current.add(output.id);
  }
  useEffect(() => {
    for (const id of freshOutputIds.current) developedImageIds.add(id);
  });
  return freshOutputIds.current;
}

/** Ticks once a second, and only while there is something to count. */
function useElapsed(since: string, active: boolean): number {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!active) return;
    setNow(Date.now());
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [active]);

  return Math.max(0, now - new Date(since).getTime());
}

/**
 * Which actions fit. The stylesheet keeps the row visible on touch, but visibility is not the
 * whole problem: six 44px targets do not fit across a phone, so the tail moves into a sheet.
 */
function useCompactActions(): boolean {
  const [compact, setCompact] = useState(() => window.matchMedia(PHONE_QUERY).matches);

  useEffect(() => {
    const query = window.matchMedia(PHONE_QUERY);
    const onChange = (event: MediaQueryListEvent) => setCompact(event.matches);
    query.addEventListener("change", onChange);
    return () => query.removeEventListener("change", onChange);
  }, []);

  return compact;
}

/**
 * How many icon buttons fit across a bar. Measured from the bar and its first button rather than
 * assumed, because the button is 34px with a mouse and 44px with a finger. Null until measured;
 * the bar clips its overflow, so the first paint shows a full row cut short rather than a wrap.
 */
function useFittedActionCount(barRef: RefObject<HTMLDivElement | null>): number | null {
  const [fit, setFit] = useState<number | null>(null);

  useEffect(() => {
    const bar = barRef.current;
    if (!bar) return;

    const measure = () => {
      const button = bar.querySelector<HTMLElement>("button");
      const size = button?.offsetWidth || 34;
      const gap = parseFloat(getComputedStyle(bar).columnGap) || 0;
      // Half a pixel of slack: a bar that is 227.6px wide holds six 34px buttons and five gaps.
      setFit(Math.max(1, Math.floor((bar.clientWidth + gap + 0.5) / (size + gap))));
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(bar);
    return () => observer.disconnect();
  }, [barRef]);

  return fit;
}
